import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import type { PartnerVisibilityStrings } from '@/i18n/partnerVisibility';
import type {
  PartnerVisibilityStatus,
  PartnerVisibilityStore,
} from '@/lib/api/types';
import { WithdrawPartnerVisibilityDialog } from './WithdrawPartnerVisibilityDialog';

export interface PartnerVisibilityMutationError {
  message: string;
  status: PartnerVisibilityStatus;
}

export interface PartnerVisibilityStoreCardProps {
  store: PartnerVisibilityStore;
  strings: PartnerVisibilityStrings;
  pending: boolean;
  error: PartnerVisibilityMutationError | null;
  status?: string | null;
  onGrant: (storeId: number) => void;
  onWithdraw: (storeId: number) => void;
  onRetry: (storeId: number, status: PartnerVisibilityStatus) => void;
}

const idsForStore = (storeId: number) => ({
  name: `partner-visibility-store-${storeId}-name`,
  description: `partner-visibility-store-${storeId}-description`,
  status: `partner-visibility-store-${storeId}-status`,
  error: `partner-visibility-store-${storeId}-error`,
});

const statusForStore = (
  store: PartnerVisibilityStore,
  strings: PartnerVisibilityStrings
) => {
  if (store.effective_status === 'granted') {
    return strings.consentGrantedStatus;
  }

  if (store.recorded_status === 'granted' && store.can_withdraw) {
    return strings.consentExpiredStatus;
  }

  if (store.availability === 'historical') {
    return strings.consentUnavailableStatus;
  }

  return strings.consentWithdrawnStatus;
};

const explanationsForStore = (
  store: PartnerVisibilityStore,
  strings: PartnerVisibilityStrings
) => {
  const explanations: string[] = [];
  const hasOldGrant =
    store.effective_status === 'withdrawn' &&
    store.recorded_status === 'granted' &&
    store.can_withdraw;

  if (store.availability === 'historical') {
    explanations.push(strings.unavailableStore);
  }
  if (hasOldGrant) {
    explanations.push(strings.expiredGrantExplanation);
  }
  if (
    store.availability === 'active' &&
    store.effective_status === 'withdrawn' &&
    !store.can_grant
  ) {
    explanations.push(strings.privacyRequestInProgressExplanation);
  }
  if (explanations.length === 0) {
    explanations.push(strings.fulfilledStoresOnlyExplanation);
  }

  return explanations;
};

export function PartnerVisibilityStoreCard({
  store,
  strings,
  pending,
  error,
  status,
  onGrant,
  onWithdraw,
  onRetry,
}: PartnerVisibilityStoreCardProps) {
  const ids = idsForStore(store.store_id);
  const checked = store.effective_status === 'granted';
  const hasOldGrant =
    !checked && store.recorded_status === 'granted' && store.can_withdraw;
  const switchDisabled =
    pending || (checked ? !store.can_withdraw : !store.can_grant);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const withdrawTriggerRef = useRef<HTMLElement | null>(null);
  const cardRef = useRef<HTMLElement | null>(null);

  const requestWithdrawal = () => {
    if (pending || !store.can_withdraw) {
      return;
    }

    withdrawTriggerRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setWithdrawDialogOpen(true);
  };

  const displayedStatus =
    pending ? strings.saving : status ?? statusForStore(store, strings);

  return (
    <article
      ref={cardRef}
      aria-labelledby={ids.name}
      className="rounded-lg border bg-card p-4 text-card-foreground"
      tabIndex={-1}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            {strings.storeNameLabel}
          </p>
          <h3
            id={ids.name}
            className="break-words font-semibold"
            dir="auto"
          >
            {store.store_name}
          </h3>
        </div>
        <Switch
          aria-busy={pending || undefined}
          aria-describedby={`${ids.description} ${ids.status}`}
          aria-errormessage={error ? ids.error : undefined}
          aria-invalid={error ? true : undefined}
          aria-label={`${strings.storeConsentSwitchLabel}: ${store.store_name}`}
          checked={checked}
          disabled={switchDisabled}
          onCheckedChange={(nextChecked) => {
            if (pending) {
              return;
            }

            if (nextChecked && !checked && store.can_grant) {
              onGrant(store.store_id);
              return;
            }

            if (!nextChecked && checked && store.can_withdraw) {
              requestWithdrawal();
            }
          }}
        />
      </div>

      <div
        id={ids.description}
        className="mt-3 space-y-2 text-sm text-muted-foreground"
      >
        {explanationsForStore(store, strings).map((explanation) => (
          <p key={explanation}>{explanation}</p>
        ))}
      </div>

      <p id={ids.status} className="mt-3 text-sm font-medium" role="status">
        {displayedStatus}
      </p>

      {hasOldGrant ? (
        <Button
          aria-label={`${strings.withdrawPreviousGrant}: ${store.store_name}`}
          className="mt-3"
          disabled={pending}
          type="button"
          variant="outline"
          onClick={requestWithdrawal}
        >
          {strings.withdrawPreviousGrant}
        </Button>
      ) : null}

      {error ? (
        <div className="mt-3 space-y-2">
          <p id={ids.error} className="text-sm text-destructive" role="alert">
            {error.message}
          </p>
          <Button
            aria-label={`${strings.retry}: ${store.store_name}`}
            disabled={pending}
            size="sm"
            type="button"
            variant="outline"
            onClick={() => onRetry(store.store_id, error.status)}
          >
            {strings.retry}
          </Button>
        </div>
      ) : null}

      <WithdrawPartnerVisibilityDialog
        store={store}
        strings={strings}
        open={withdrawDialogOpen}
        pending={pending}
        fallbackFocusElement={cardRef.current}
        returnFocusElement={withdrawTriggerRef.current}
        onOpenChange={setWithdrawDialogOpen}
        onConfirm={onWithdraw}
      />
    </article>
  );
}
